import { validateCpr, validateCprAge, validateEmail, validatePhone } from '@flos/react-ui';
import type {
    AccidentYoungAndOtherPersonMissingInformationDTO,
    CarMissingInformationDTO,
    ChildAccidentMissingInformationDTO,
    ContentMissingInformationDTO,
    MissingInformationDTO,
    PetMissingInformationDTO,
    PrivateHouseMissingInformationDTO,
    SingleTripMissingInformationDTO,
    CaravanMissingInformationDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/missingInformation';
import type { CaravanDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/uvp';
import { replaceAllCharactersButNumbers } from '../../../utils/stringUtil';
import type { AllCoverablesDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';

/*
 * TODO : According to Sales Force, only a single word is accepted.
 * However, according the Danish rule of surname then you can have
 * dash (-), space ( ) and apostrophe (').
 * Space and apostrophe is not supported right now until SF is ready.
 */
const validateSurname = (surname: string | undefined): boolean => {
    return /^[A-Za-zÆØÅÄÖÜæøåáàéèäöüóòúùð-]+$/g.test(surname ?? '');
};

const isMissingPetChipIdValidated = (data: PetMissingInformationDTO): boolean => {
    return data.hasNoChipNumber ? true : !!data.chipNumber;
};

const isMissingPoliceDogIdValidated = (data: PetMissingInformationDTO): boolean => {
    if (!data.dogUse || data.dogUse !== 'Tjenestehund') return true;
    if (data.policeDogId) return true;
    return false;
};

const isMissingPetDTOValidated = (data: PetMissingInformationDTO): boolean => {
    return isMissingPetChipIdValidated(data) && isMissingPoliceDogIdValidated(data);
};

const isMissingSingleTripDTOValidated = (data: SingleTripMissingInformationDTO): boolean => {
    return (
        !!data.address?.addressLine1 &&
        !!data.address.addressLine2 &&
        !!data.address.city &&
        !!data.address.postalCode &&
        !!data.name &&
        !!data.surname &&
        validateCpr(data.cpr)
    );
};

const isContainingDuplicateCPR = (data: ChildAccidentMissingInformationDTO[]): boolean => {
    const filteredArray = data.filter((item) => item.cpr).map((item) => item.cpr);
    const cprSet = new Set(filteredArray);
    return cprSet.size !== filteredArray.length;
};

const areAllChildAccidentsValidated = (data: ChildAccidentMissingInformationDTO[]): boolean => {
    if (isContainingDuplicateCPR(data)) return false;

    if (data.some((item) => !validateCpr(replaceAllCharactersButNumbers(item.cpr ?? ''))))
        return false;

    if (data.some((item) => !item.dawaId)) return false;

    return true;
};

const isMissingPrivateContentDTOValidated = (data: ContentMissingInformationDTO): boolean => {
    return !!data.name && validateSurname(data.surname) && validateCpr(data.cpr);
};

const areAllAccidentOtherPersonDTOValidated = (
    data: AccidentYoungAndOtherPersonMissingInformationDTO[]
): boolean => {
    if (isContainingDuplicateCPR(data)) return false;

    if (data.some((item) => !validateCpr(replaceAllCharactersButNumbers(item.cpr ?? ''))))
        return false;

    if (data.some((item) => !item.dawaId)) return false;

    if (data.some((item) => !validatePhone(item.phoneNumber))) return false;

    if (data.some((item) => item.email && !validateEmail(item.email))) return false;

    return true;
};

const areAllCarDTOValidated = (data: CarMissingInformationDTO[]): boolean => {
    if (
        data.some(
            (item) => item.hasOdometer === undefined || (!item.odometerReading && item.hasOdometer)
        )
    ) {
        return false;
    }

    return true;
};

const areAllCaravanDTOValidated = (
    data: CaravanMissingInformationDTO[],
    coverable: CaravanDTO[]
): boolean => {
    return data.some((missingInfo) => {
        const currentCoverableIndex =
            coverable.findIndex((coverable) => coverable.fixedId === missingInfo.fixedId) ?? -1;
        const currentCoverable = coverable[currentCoverableIndex];

        return !(currentCoverable.stationary && !currentCoverable.regNumber && !missingInfo.vin);
    });
};

const areAnyChildrenAbove18 = (data: ChildAccidentMissingInformationDTO[]): boolean => {
    if (data.length === 0) return false;

    return data.some(
        // validateCprAge needs to be verified once the bug from C9 is fixed
        (missingChildDto) => validateCprAge(missingChildDto.cpr ?? '', { maxAge: 17 }) === 'TOO_OLD'
    );
};

const areAnyChildren40DaysFrom18 = (data: ChildAccidentMissingInformationDTO[]): boolean => {
    if (data.length === 0) return false;

    const compareDate = new Date();
    compareDate.setDate(compareDate.getDate() + 40);
    return data.some(
        (missingChildDto) =>
            //  TODO: Change maxAge to 18 once bug from C9 is fixed
            validateCprAge(missingChildDto.cpr ?? '', {
                maxAge: 17,
                currentTime: compareDate.getTime()
            }) === 'TOO_OLD'
    );
};

const areAnyYoungAccident40DaysFrom28OrAbove = (
    data: AccidentYoungAndOtherPersonMissingInformationDTO[]
): boolean => {
    if (data.length === 0) return false;

    const compareDate = new Date();
    compareDate.setDate(compareDate.getDate() + 40);
    return data.some(
        (missingDto) =>
            missingDto.flavourName === 'Ulykkesforsikring til unge' &&
            //  TODO: Change maxAge to 28 once bug from C9 is fixed
            validateCprAge(missingDto.cpr ?? '', {
                maxAge: 27,
                currentTime: compareDate.getTime()
            }) === 'TOO_OLD'
    );
};

const areAllHouseDTOValidated = (data: PrivateHouseMissingInformationDTO[]): boolean => {
    return !data.some((missingInfo) => {
        return missingInfo.hasBuildingReportChoice === undefined;
    });
};

const isMissingInformationDTOValidated = (
    missingInformation: MissingInformationDTO,
    coverables: AllCoverablesDTO
): boolean => {
    if (!missingInformation) return false;
    for (const key of Object.keys(missingInformation)) {
        switch (key as keyof MissingInformationDTO) {
            case 'pet':
                if (missingInformation.pet?.some((item) => !isMissingPetDTOValidated(item)))
                    return false;
                break;
            case 'singleTripTravel':
                if (
                    missingInformation.singleTripTravel?.some(
                        (item) => !isMissingSingleTripDTOValidated(item)
                    )
                )
                    return false;
                break;
            case 'singleTripCancellation':
                if (
                    missingInformation.singleTripCancellation?.some(
                        (item) => !isMissingSingleTripDTOValidated(item)
                    )
                )
                    return false;
                break;
            case 'childAccident':
                if (!areAllChildAccidentsValidated(missingInformation.childAccident ?? []))
                    return false;
                break;
            case 'contentCoinsured':
                if (
                    missingInformation.contentCoinsured?.some(
                        (item) => !isMissingPrivateContentDTOValidated(item)
                    )
                )
                    return false;
                break;
            case 'contentCohousing':
                if (
                    missingInformation.contentCohousing?.some(
                        (item) => !isMissingPrivateContentDTOValidated(item)
                    )
                )
                    return false;
                break;
            case 'accidentOtherPerson':
                if (
                    !areAllAccidentOtherPersonDTOValidated(
                        missingInformation.accidentOtherPerson ?? []
                    )
                )
                    return false;
                break;
            case 'car':
                if (!areAllCarDTOValidated(missingInformation.car ?? [])) return false;
                break;
            case 'privateHouse': {
                if (!areAllHouseDTOValidated(missingInformation.privateHouse ?? [])) return false;
                break;
            }
            case 'caravan': {
                if (
                    missingInformation.caravan?.some(
                        (item) =>
                            !areAllCaravanDTOValidated(
                                missingInformation.caravan ?? [],
                                coverables.caravans ?? []
                            )
                    )
                )
                    return false;
                break;
            }
        }
    }
    return true;
};

export {
    isMissingPetChipIdValidated,
    isMissingPoliceDogIdValidated,
    isMissingPetDTOValidated,
    isMissingSingleTripDTOValidated,
    isMissingInformationDTOValidated,
    areAllChildAccidentsValidated,
    isMissingPrivateContentDTOValidated,
    isContainingDuplicateCPR,
    areAllAccidentOtherPersonDTOValidated,
    areAnyChildrenAbove18,
    areAnyChildren40DaysFrom18,
    areAnyYoungAccident40DaysFrom28OrAbove,
    areAllCarDTOValidated,
    areAllHouseDTOValidated,
    areAllCaravanDTOValidated
};
